import React, { useMemo, useState } from 'react';
import { gql, useLazyQuery, useQuery } from '@common/hooks';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import {
  MultiSelectTableFilter,
  FilteredTable,
} from 'works/components/filtered-table';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import {
  BaseButton,
  CSVLink,
  DateRangeFilter,
  ErrorHandler,
  Icon,
  Link,
} from '@common/components';
import { isEmpty, startCase } from 'lodash';
import { RenderTableColumn } from 'works/components/filtered-table/render-filtered-table';
import { SorterResult } from 'antd/lib/table/interface';
import {
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import { Colors } from 'works/legacy/lib/style-helper';
import {
  ASSIGNMENT_STATE_INFO,
  AssignmentStatePill,
} from 'works/components/assignment-state-pill';
import {
  NormalizedSorterResult,
  normalizeSortingData,
} from 'works/legacy/lib/table-filters-helper';
import { buildDialogUrl } from '@common/services';

const COMPLIANCE_ITEM_STATES = {
  pending: {
    label: 'Pending Upload',
    color: Colors.steelDarkest,
  },
  submitted: {
    label: 'Pending Verification',
    color: Colors.sunDarkest,
  },
  approved: {
    label: 'Approved',
    color: Colors.meadowDarker,
  },
  rejected: {
    label: 'Rejected',
    color: Colors.coralDarker,
  },
};

const WorksListItemsQuery = gql`
  query PaginatedWorksListItems(
    $filters: PaginatedWorksListItemFiltersInput!
    $orders: WorksListItemsOrderField
  ) {
    paginatedWorksListItems(filters: $filters, orders: $orders) {
      worksListItems {
        id
        assignmentId
        workerId
        assignmentState
        clinicalUnitName
        completedOn
        expiresOn
        revisedExpiresOn
        name
        organizationBusinessName
        organizationUnitName
        staffingRequestType
        complianceState
        supplierName
        teamMember
      }
    }
  }
`;

const FilterDataQuery = gql`
  query PaginatedWorksListItemsFilterData {
    organizationBusinesses {
      id
      name
    }
    worksClinicalUnits {
      id
      displayName
    }
    activeWorksListItemNames
    suppliers {
      id
      name
    }
  }
`;

type PaginatedWorksListItem = {
  id: string;
  assignmentId: string;
  workerId?: string;
  name: string;
  completedOn: string;
  expiresOn: string;
  revisedExpiresOn: string;
  supplierName: string;
  organizationBusinessName: string;
  organizationName: string;
  organizationUnitName: string;
  clinicalUnitName: string;
  staffingRequestType: string;
  teamMember: string;
  complianceState: string;
  assignmentState: string;
};

type WorksListItemsVariables = {
  filters: {
    [key: string]: unknown;
  };
  orders: NormalizedSorterResult;
};

type WorksListItemsResult = {
  paginatedWorksListItems: {
    worksListItems: PaginatedWorksListItem[];
  };
};

type FilterData = {
  worksClinicalUnits: {
    id: string;
    displayName: string;
  }[];
  activeWorksListItemNames: string[];
  suppliers: {
    id: string;
    name: string;
  }[];
  organizationBusinesses: {
    id: string;
    name: string;
  }[];
};

type Filters = {
  name?: string[];
  completedOn?: string[];
  expiresOn?: string[];
  revisedExpiresOn?: string[];
  organizationBusiness?: string[];
  clinicalUnit?: string[];
  supplier?: string[];
  staffingRequestType?: string[];
  teamMember?: string[];
  complianceState?: string[];
  assignmentState?: string[];
  expiringSoon?: boolean;
  expired?: boolean;
};

const sorter = {
  multiple: 1,
  compare: () => 0,
};

function organizationCompliancePath(record: PaginatedWorksListItem) {
  if (record.staffingRequestType === 'long_term')
    return `/works/team/long_term/${record.assignmentId}/compliance`;

  return `/works/team/on_demand/${record.workerId}/assignments/${record.assignmentId}/compliance`;
}

function getColumns(
  entityType: EntityType,
  filters: Filters,
  data?: FilterData
): RenderTableColumn<PaginatedWorksListItem>[] {
  const isOrganization = entityType === 'organization';
  const entityColumn: RenderTableColumn<PaginatedWorksListItem> = isOrganization
    ? {
        key: 'supplier',
        dataIndex: 'supplierName',
        title: 'Supplier',
        sorter,
        filtered: !!filters.supplier || false,
        filteredValue: filters.supplier || null,
        filters: (data?.suppliers || []).map((supplier) => ({
          value: supplier.id,
          text: supplier.name,
        })),
        filterDropdown: (values) => MultiSelectTableFilter(values),
      }
    : {
        key: 'organizationBusiness',
        dataIndex: 'organizationBusinessName',
        title: 'Facility',
        sorter,
        filtered: !!filters.organizationBusiness || false,
        filteredValue: filters.organizationBusiness || null,
        filters: (data?.organizationBusinesses || []).map((business) => ({
          value: business.id,
          text: business.name,
        })),
        filterDropdown: (values) => MultiSelectTableFilter(values),
      };

  return [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Item Name',
      sorter,
      filtered: !!filters.name,
      filteredValue: filters.name || null,
      filters: data?.activeWorksListItemNames?.map((name) => ({
        value: name,
        text: name,
      })),
      filterDropdown: MultiSelectTableFilter,
      render: (value: string, complianceItem: PaginatedWorksListItem) => {
        return (
          <Link
            className="compliance-table-row-link"
            to={buildDialogUrl('compliance-item-drawer', {
              props: { id: complianceItem.id },
            })}
          >
            {value}
          </Link>
        );
      },
    },
    {
      key: 'teamMember',
      dataIndex: 'teamMember',
      title: 'Team Member',
      sorter,
      filtered: !!filters.teamMember,
      filteredValue: filters.teamMember,
      filterDropdown: TextSearchTableFilter,
      className: 'sentry-mask',
      render: (teamMember, record) => {
        const compliancePath = isOrganization
          ? organizationCompliancePath(record)
          : `/works/team/${record.assignmentId}/compliance`;

        return (
          <Link className="compliance-table-row-link" to={compliancePath}>
            {teamMember}
          </Link>
        );
      },
    },
    {
      key: 'completedOn',
      dataIndex: 'completedOn',
      title: 'Completion',
      sorter,
      filterFormat: 'dateRange',
      filtered: !!filters.completedOn,
      filteredValue: filters.completedOn || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: 12 }}>
          <DateRangeFilter
            autoFocus
            onSearch={(value) => {
              setSelectedKeys((value || []) as string[]);
              confirm();
            }}
          />
        </section>
      ),
      render: (completedOn) =>
        completedOn && toUSCompactDateFormat(completedOn),
    },
    {
      key: 'expiresOn',
      dataIndex: 'expiresOn',
      title: 'Expiration',
      sorter,
      filterFormat: 'dateRange',
      filtered: !!filters.expiresOn,
      filteredValue: filters.expiresOn || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: 12 }}>
          <DateRangeFilter
            autoFocus
            onSearch={(value) => {
              setSelectedKeys((value || []) as string[]);
              confirm();
            }}
          />
        </section>
      ),
      render: (expiresOn) => {
        if (expiresOn) {
          return toUSCompactDateFormat(expiresOn);
        }

        return 'N/A';
      },
    },
    {
      key: 'revisedExpiresOn',
      dataIndex: 'revisedExpiresOn',
      title: 'New Expiration',
      sorter,
      filterFormat: 'dateRange',
      filtered: !!filters.revisedExpiresOn,
      filteredValue: filters.revisedExpiresOn || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: 12 }}>
          <DateRangeFilter
            autoFocus
            onSearch={(value) => {
              setSelectedKeys((value || []) as string[]);
              confirm();
            }}
          />
        </section>
      ),
      render: (revisedExpiresOn) =>
        revisedExpiresOn && toUSCompactDateFormat(revisedExpiresOn),
    },
    entityColumn,
    {
      key: 'clinicalUnit',
      dataIndex: 'clinicalUnitName',
      title: 'Unit',
      sorter,
      filterDropdown: MultiSelectTableFilter,
      filtered: !!filters.clinicalUnit,
      filteredValue: filters.clinicalUnit || null,
      filters: (data?.worksClinicalUnits || []).map((clinicalUnit) => ({
        value: clinicalUnit.id,
        text: clinicalUnit.displayName,
      })),
      render: (value: string, record: PaginatedWorksListItem) => (
        <div>
          <div>{value}</div>
          <span className="secondary-paragraph">
            {record.organizationBusinessName}
          </span>
        </div>
      ),
    },
    {
      key: 'staffingRequestType',
      dataIndex: 'staffingRequestType',
      title: 'Type',
      sorter,
      filtered: !!filters.staffingRequestType,
      filteredValue: filters.staffingRequestType || null,
      filters: [
        {
          text: 'On Demand',
          value: 'on_demand',
        },
        {
          text: 'Long Term',
          value: 'long_term',
        },
      ],
      render: (staffingRequestType) => startCase(staffingRequestType),
    },
    {
      key: 'complianceState',
      dataIndex: 'complianceState',
      title: 'Item Status',
      sorter,
      filtered: !!filters.complianceState,
      filteredValue: filters.complianceState,
      filterDropdown: MultiSelectTableFilter,
      filters: Object.entries(COMPLIANCE_ITEM_STATES).map(([key, item]) => ({
        text: item.label,
        value: key,
      })),
      render: (complianceState) => {
        const stateModel =
          COMPLIANCE_ITEM_STATES[
            complianceState as keyof typeof COMPLIANCE_ITEM_STATES
          ];

        return (
          <span style={{ color: stateModel.color }}>{stateModel.label}</span>
        );
      },
    },
    {
      key: 'assignmentState',
      dataIndex: 'assignmentState',
      title: 'Assignment Status',
      sorter,
      filteredValue: filters.assignmentState,
      filtered: !!filters.assignmentState,
      filterDropdown: MultiSelectTableFilter,
      filters: [
        {
          text: 'Onboarding',
          value: 'onboarding',
        },
        {
          text: 'Cleared',
          value: 'cleared',
        },
        {
          text: 'Working',
          value: 'working',
        },
      ],
      render: (assignmentState) => {
        return <AssignmentStatePill state={assignmentState} />;
      },
    },
  ];
}

type EntityType = 'supplier' | 'organization';

type ComplianceTableProps = {
  entity: EntityType;
  // @TODO: use default filter prop to filter query
  defaultFilter: Filters;
  defaultOrder: NormalizedSorterResult;
  filterKey: string;
};

function mergeFilters(filter: Filters, other: Filters): Filters {
  const result: Filters = { ...filter };

  let key: keyof Filters;
  for (key in other) {
    if (other[key]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result[key] = other[key] as any;
    }
  }

  return result;
}

export function ComplianceTable({
  entity,
  defaultFilter,
  defaultOrder,
  filterKey,
}: ComplianceTableProps) {
  const [filters, setFilters] = useStoredFilters(filterKey, {
    store: localStorageStore,
  });

  const [downloading, setDownloading] = useState(false);

  const [fetchFiltersData, { error: errorFilterData, data: filterData }] =
    useLazyQuery<FilterData>(FilterDataQuery);

  const { loading, error, fetchMore, data } = useQuery<
    WorksListItemsResult,
    WorksListItemsVariables
  >(WorksListItemsQuery, {
    variables: {
      filters: mergeFilters(defaultFilter, filters),
      orders: defaultOrder,
    },
    onCompleted() {
      fetchFiltersData();
    },
  });

  const csvData = useMemo(() => {
    if (!data) return [];

    const isOrganization = entity === 'organization';
    const header = [
      'Name',
      'Team Member',
      'Completion',
      'Expiration',
      'New Expiration',
      isOrganization ? 'Supplier' : 'Facility',
      'Unit',
      'Type',
      'Item Status',
      'Assignment Status',
    ];

    const csv = data.paginatedWorksListItems.worksListItems.map((item) => [
      item.name,
      item.teamMember,
      item.completedOn,
      item.expiresOn,
      item.revisedExpiresOn,
      isOrganization ? item.supplierName : item.organizationBusinessName,
      item.clinicalUnitName,
      startCase(item.staffingRequestType),
      COMPLIANCE_ITEM_STATES[
        item.complianceState as keyof typeof COMPLIANCE_ITEM_STATES
      ].label,
      ASSIGNMENT_STATE_INFO[
        item.assignmentState as keyof typeof ASSIGNMENT_STATE_INFO
      ].label,
    ]);

    return [header, ...csv];
  }, [data, entity]);

  const columns = useMemo(
    () => getColumns(entity, filters, filterData),
    [entity, filters, filterData]
  );

  async function fakeDownloadTimer() {
    setDownloading(true);
    await new Promise((res) => setTimeout(res, 1000));
    setDownloading(false);
  }

  function onTableChange(
    _pagination: object,
    filters: object,
    sorter: SorterResult<PaginatedWorksListItem>
  ) {
    let orders: NormalizedSorterResult = normalizeSortingData(sorter);

    if (isEmpty(orders)) {
      orders = defaultOrder;
    }

    fetchMore({
      variables: {
        filters: mergeFilters(defaultFilter, filters),
        orders,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (fetchMoreResult) return fetchMoreResult;

        return prev;
      },
    });
  }

  if (error || errorFilterData) {
    return (
      <ErrorHandler error={error || errorFilterData} log="ComplianceTable" />
    );
  }

  const stamp = toUSCompactDateFormat(Date.now());
  const fileName = `Works - Compliance - ${stamp}.csv`;

  return (
    <>
      {/* We could try a Portal for this instead of the negative margin */}
      <CSVLink
        data={csvData}
        fileName={fileName}
        style={{ float: 'right', marginTop: -60 }}
      >
        <BaseButton
          loading={downloading}
          disabled={loading || downloading}
          onClick={fakeDownloadTimer}
        >
          <Icon icon="fileExport" kind="light" size="md" />
          Export
        </BaseButton>
      </CSVLink>
      <FilteredTable
        rowKey="id"
        className="compliance-table"
        filtersDataReady={!!filterData}
        pagination={{
          hideOnSinglePage: true,
          pageSize: data?.paginatedWorksListItems.worksListItems.length,
          total: data?.paginatedWorksListItems.worksListItems.length,
        }}
        loading={loading}
        columns={columns}
        dataSource={data?.paginatedWorksListItems.worksListItems}
        filters={filters}
        onFilterChange={setFilters}
        onChange={onTableChange}
        localStorageFilterKey={filterKey}
      />
      <style jsx>{`
        :global(.compliance-table .ant-table-tbody > tr > td) {
          padding: 8px 4px 8px 8px;
        }
        :global(td > a.compliance-table-row-link) {
          cursor: pointer;
          padding: 18px 12px 18px 0;
          font-weight: 800;
        }
      `}</style>
    </>
  );
}
